import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { handleSmoothScroll } from "../scroll";
import "./Navbar.scss";
import { useLang } from "../../../shared/i18n/LanguageContext";
import Logo from "../Logo/Logo";

interface NavbarProps {
  onZoomNavigate?: (e: React.MouseEvent, to: string, bg?: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ onZoomNavigate }) => {
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const isLightPage = ["/about", "/contact", "/articles", "/how-it-works", "/security", "/pricing", "/faq"].some(p => location.pathname.startsWith(p));
  const [isScrolled, setIsScrolled] = useState(isLightPage);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMenuOpenRef = useRef(isMenuOpen);
  const { t, lang, setLang } = useLang();

  // Update ref synchronously during render to prevent race conditions with scroll events
  isMenuOpenRef.current = isMenuOpen;

  useEffect(() => {
    if (!isMenuOpen) {
      setIsScrolled(isLightPage || window.scrollY > 50);
    }
  }, [isMenuOpen, isLightPage]);

  useEffect(() => {
    const handleScroll = () => {
      // Don't update scroll state while menu is open to prevent flicker
      // when body overflow: hidden causes layout shifts
      if (!isMenuOpenRef.current) {
        setIsScrolled(isLightPage || window.scrollY > 50);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetId: string,
  ) => {
    if (!isLanding) {
      e.preventDefault();
      window.location.href = `/#${targetId}`;
      return;
    }
    handleSmoothScroll(e, targetId);
    setIsMenuOpen(false);
  };

  return (
    <div className="nav-wrapper">
      <nav
        className={`nav-editorial ${isScrolled ? "scrolled" : ""} ${isMenuOpen ? "menu-open" : ""}`}
      >
        <div className="nav-inner">
          <Logo
            variant="nav"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              if (window.location.pathname !== "/") {
                onZoomNavigate?.(
                  e,
                  "/",
                  "linear-gradient(135deg, #065f46 0%, #047857 100%)",
                );
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          />

          <ul className="nav-links desktop-only">
                <li>
                  <a href={isLanding ? "#how" : "/#how"} onClick={(e) => handleNavClick(e, "how")}>
                    {t("navHowItWorks")}
                  </a>
                </li>
                <li>
                  <a href={isLanding ? "#features" : "/#features"} onClick={(e) => handleNavClick(e, "features")}>
                    {t("navFeatures")}
                  </a>
                </li>
                <li>
                  <a href={isLanding ? "#security" : "/#security"} onClick={(e) => handleNavClick(e, "security")}>
                    {t("navSecurity")}
                  </a>
                </li>
                <li>
                  <a href={isLanding ? "#pricing" : "/#pricing"} onClick={(e) => handleNavClick(e, "pricing")}>
                    {t("navPricing")}
                  </a>
                </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (window.location.pathname === "/about") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    onZoomNavigate?.(
                      e,
                      "/about",
                      "linear-gradient(135deg, #065f46 0%, #047857 100%)",
                    );
                  }
                }}
              >
                {t("navAbout")}
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (window.location.pathname === "/contact") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    onZoomNavigate?.(
                      e,
                      "/contact",
                      "linear-gradient(135deg, #065f46 0%, #047857 100%)",
                    );
                  }
                }}
              >
                {t("navContactUs")}
              </a>
            </li>
          </ul>

          <div className="nav-actions desktop-only">
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value as any)}
              className="nav-lang-select"
              aria-label="Select language"
            >
              <option value="en">EN</option>
              <option value="de">DE</option>
            </select>
            <a
              href="#"
              className="btn-nav-ghost"
              onClick={(e) => {
                e.preventDefault();
                onZoomNavigate?.(
                  e,
                  "/login",
                  "linear-gradient(135deg, #065f46 0%, #047857 100%)",
                );
              }}
            >
              {t("navSignIn")}
            </a>
            <a
              href="#"
              className="btn-nav-solid cta-primary"
              onClick={(e) => {
                e.preventDefault();
                onZoomNavigate?.(
                  e,
                  "/register",
                  // "linear-gradient(135deg, #065f46 0%, #047857 100%)",
                  "#fff",
                );
              }}
            >
              {t("navStartFree")}
            </a>
          </div>

          <button
            className={`hamburger-btn ${isMenuOpen ? "open" : ""}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {/* {isMenuOpen && ( */}
      <div
        className={`nav-mobile-overlay ${isMenuOpen ? "open" : ""}`}
        aria-hidden={!isMenuOpen}
        {...(!isMenuOpen ? { inert: "" } : {})}
      >
        <div className="nav-mobile-inner">
          <ul className="nav-mobile-links">
            <li>
              <a href={isLanding ? "#how" : "/#how"} onClick={(e) => handleNavClick(e, "how")}>
                {t("navHowItWorks")}
              </a>
            </li>
            <li>
              <a
                href={isLanding ? "#features" : "/#features"}
                onClick={(e) => handleNavClick(e, "features")}
              >
                {t("navFeatures")}
              </a>
            </li>
            <li>
              <a href={isLanding ? "#pricing" : "/#pricing"} onClick={(e) => handleNavClick(e, "pricing")}>
                {t("navPricing")}
              </a>
            </li>
            <li>
              <a
                href={isLanding ? "#security" : "/#security"}
                onClick={(e) => handleNavClick(e, "security")}
              >
                {t("navSecurity")}
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (window.location.pathname === "/about") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    onZoomNavigate?.(e, "/about", "#fff");
                  }
                  setIsMenuOpen(false);
                }}
              >
                {t("navAbout")}
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (window.location.pathname === "/contact") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    onZoomNavigate?.(e, "/contact", "#fff");
                  }
                  setIsMenuOpen(false);
                }}
              >
                {t("navContactUs")}
              </a>
            </li>
          </ul>

          <div className="nav-mobile-footer">
            <div className="nav-mobile-lang">
              <span className="mobile-lang-label">Language</span>
              <div className="mobile-lang-options">
                <button
                  className={lang === "en" ? "active" : ""}
                  onClick={() => {
                    setLang("en");
                    setIsMenuOpen(false);
                  }}
                >
                  EN
                </button>
                <button
                  className={lang === "de" ? "active" : ""}
                  onClick={() => {
                    setLang("de");
                    setIsMenuOpen(false);
                  }}
                >
                  DE
                </button>
              </div>
            </div>

            <div className="nav-mobile-actions">
              <a
                href="#"
                className="btn-mobile-ghost"
                onClick={(e) => {
                  e.preventDefault();
                  onZoomNavigate?.(
                    e,
                    "/login",
                    "linear-gradient(135deg, #065f46 0%, #047857 100%)",
                  );
                }}
              >
                {t("navSignIn")}
              </a>
              <a
                href="#"
                className="btn-mobile-solid"
                onClick={(e) => {
                  e.preventDefault();
                  onZoomNavigate?.(
                    e,
                    "/register",
                    "linear-gradient(135deg, #065f46 0%, #047857 100%)",
                  );
                }}
              >
                {t("navStartFree")}
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* )} */}
    </div>
  );
};

export default Navbar;
